import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Map, ShieldCheck } from "lucide-react";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

import CountryPhoneInput from "../components/CountryPhoneInput";
import OtpInput from "../components/OtpInput";
import { api, setToken } from "../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [country, setCountry] = useState<CountryCode>("AE");
  const [national, setNational] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifiedOnceRef = useRef(false);

  function buildE164(): string | null {
    const trimmed = national.replace(/[^\d]/g, "");
    if (!trimmed) return null;
    const candidate = `+${getCountryCallingCode(country)}${trimmed}`;
    const parsed = parsePhoneNumberFromString(candidate, country);
    if (parsed && parsed.isValid()) return parsed.number;
    return candidate;
  }

  const e164 = buildE164();

  async function sendOtp(e?: React.FormEvent) {
    e?.preventDefault();
    if (!e164) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/send-otp", { phone: e164 });
      setStep("code");
    } catch {
      setError("Failed to send code. Please verify the phone number is enrolled.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(currentCode: string, e?: React.FormEvent) {
    e?.preventDefault();
    if (!e164) return;
    if (currentCode.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ token: string }>("/api/auth/verify-otp", {
        phone: e164,
        code: currentCode,
      });
      setToken(res.token);
      navigate("/");
    } catch {
      setError("Code is incorrect or expired.");
      verifiedOnceRef.current = false;
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (step !== "code") return;
    if (code.length === 6 && !verifiedOnceRef.current && !busy) {
      verifiedOnceRef.current = true;
      void verifyOtp(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step]);

  return (
    <div className="min-h-full flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-[var(--color-surface)]/80 backdrop-blur ring-1 ring-[var(--color-border)] p-6 shadow-2xl">
        <div className="flex items-center gap-2 text-sky-300">
          <span className="inline-flex w-9 h-9 rounded-md bg-sky-500/20 ring-1 ring-sky-400/30 items-center justify-center">
            <Map size={18} />
          </span>
          <div>
            <div className="text-lg font-semibold leading-tight">Travel Tracking</div>
            <div className="text-xs text-slate-400">Employee safety dashboard</div>
          </div>
        </div>

        <h1 className="mt-6 text-xl font-semibold text-slate-100">Sign in</h1>
        <p className="text-sm text-slate-400 mt-1">
          We'll send a one-time code to your WhatsApp.
        </p>

        {step === "phone" && (
          <form onSubmit={sendOtp} className="mt-5 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">
                Phone number
              </span>
              <div className="mt-1">
                <CountryPhoneInput
                  country={country}
                  national={national}
                  onCountryChange={setCountry}
                  onNationalChange={setNational}
                  disabled={busy}
                  autoFocus
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={busy || !e164 || national.length < 6}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white px-4 py-2 font-medium"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
              Send code
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={(e) => verifyOtp(code, e)} className="mt-5 space-y-4">
            <div className="text-sm text-slate-300 text-center">
              Code sent to{" "}
              <span className="text-slate-100 font-medium">{e164}</span>
            </div>

            <OtpInput
              value={code}
              onChange={(next) => {
                verifiedOnceRef.current = false;
                setCode(next);
              }}
              autoFocus
              disabled={busy}
              hasError={!!error}
            />

            {busy && (
              <div className="text-xs text-slate-400 text-center inline-flex items-center justify-center gap-1.5 w-full">
                <Loader2 size={12} className="animate-spin" /> Verifying…
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setStep("phone");
                setCode("");
                setError(null);
                verifiedOnceRef.current = false;
              }}
              className="w-full text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
            >
              Use a different phone
            </button>
          </form>
        )}

        {error && (
          <div className="mt-4 text-sm text-red-300 bg-red-500/10 ring-1 ring-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
